import { useEffect, useRef } from "react";

// Hook unificado para lectores de código de barras / QR USB (pistola).
// Los lectores actúan como un teclado que "teclea" el código muy rápido y termina en Enter.
// Este hook detecta esas ráfagas rápidas (gap corto entre teclas) terminadas en Enter y
// dispara `onScan(code)`. Ignora eventos originados en campos editables (INPUT/TEXTAREA/
// SELECT/contentEditable) para no interferir con la captura manual.
//
// Se comporta IDÉNTICO en Caseta (Terminal) y Valet. Usa un ref para `onScan` de modo que
// siempre se invoque la última versión de la función (evita stale closures) sin re-suscribir
// el listener en cada render.
//
// options:
//   - onScan(code): callback con el código leído (ya .trim()).
//   - enabled: si es false no se suscribe (ej. Caseta solo con turno abierto).
//   - gap: ms máximos entre teclas para considerarlo escáner (tecleo humano es más lento).
//   - minLength: longitud mínima del código para disparar onScan.
export function useBarcodeScanner({ onScan, enabled = true, gap = 60, minLength = 3 } = {}) {
  const onScanRef = useRef(onScan);
  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    if (!enabled) return undefined;
    let buffer = "";
    let last = 0;
    const isEditable = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
    };
    const onKey = (e) => {
      if (isEditable(document.activeElement) || isEditable(e.target)) return;
      const now = Date.now();
      if (now - last > gap) buffer = ""; // pausa larga -> tecleo humano, reinicia buffer
      last = now;
      if (e.key === "Enter") {
        const code = buffer.trim();
        buffer = "";
        if (code.length >= minLength) {
          e.preventDefault();
          onScanRef.current?.(code);
        }
        return;
      }
      if (e.key.length === 1) buffer += e.key; // solo caracteres imprimibles
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled, gap, minLength]);
}
